import { createSlice } from "@reduxjs/toolkit";
import Permissions from "src/permision.json";
import axios from "src/utils/axios";
import { getRolesAPI } from "./meeting";
import { getParticipants } from "./events/Resource_Assignment";

interface IParticipant {
  id: string;
  name: string;
  emailId: string;
  role: {
    id: string;
    name: string;
  };
}

interface IDefaultRole {
  id?: string;
  name?: string;
}

interface IOptions {
  allowed: boolean,
  name: string;
  id: string;
}

interface IRoleChange {
  msgId: string;
  data: {
      oldRole: string;
      newRole: string;
      message: string;
  }
}

interface IRoot {
  participantsData: IParticipant[];
  participantDetails: IParticipant;
  drawerOpen: boolean;
  defaultRole: IDefaultRole;
  roles: IOptions[];
  showLoader: boolean;
  roleUpdate : IRoleChange;
  unmuteAll:string;
}

const initialState: IRoot = {
  participantsData: [],
  participantDetails: {
    id: "",
    name: "",
    emailId: "",
    role: {
      id: "",
      name: "",
    },
  },
  drawerOpen: false,
  roles: [],
  defaultRole: {},
  showLoader: false,
  roleUpdate: {
    msgId: '',
    data: {
        oldRole: '',
        newRole: '',
        message: ''
    }
  },
  unmuteAll:""
};

const slice = createSlice({
  name: "participants",
  initialState,
  reducers: {
    setParticipantsDrawer(state, action) {
      state.drawerOpen = action.payload;
    },
    setParticipantsData(state, action) {
      state.participantsData = action.payload;
    },
    setParticipantDetails(state, action) {
      state.participantDetails[action.payload.key] = action.payload.value;
    },
    DefaultRole(state, action) {
      state.defaultRole = action.payload;
    },
    RolesOptions(state, action) {
      state.roles = action.payload;
    },
    setShowLoader(state, action) {
      state.showLoader = action.payload;
    },
    resetParticipantDetails(state, action) {
      state.participantDetails = action.payload;
    },
    showRefreshDialogRoleChange(state, action) {
      state.roleUpdate = action.payload;
    },
    setUnmuteAll(state, action) {
      state.unmuteAll = action.payload;
    },
  },
});

const BASE_URL = process.env.REACT_APP_API_URL;

export const changeParticipantDetails = (data: any) => (dispatch) => {
  dispatch(slice.actions.setParticipantDetails(data));
};

export const setParticipantsDrawer = (data: boolean) => (dispatch) => {
  dispatch(slice.actions.setParticipantsDrawer(data));
};

export const setParticipantsData = (data: any) => (dispatch) => {
  dispatch(slice.actions.setParticipantsData(data));
};

export const resetParticipantDetails = (participantDetails) => (dispatch) => {
  dispatch(slice.actions.resetParticipantDetails(participantDetails));
};

export const roleUpdateParticipant = (roleDetails) => async (dispatch) => {
  dispatch(slice.actions.showRefreshDialogRoleChange(roleDetails));
};
export const unMuteAllParticipants = (data) => async (dispatch) => {
  dispatch(slice.actions.setUnmuteAll(data));
};

export const getParticipantsData = () => async (dispatch, getState) => {
  const { allowedPermissions } = getState().AVSetting.allowedPermissions;
  if (allowedPermissions && !allowedPermissions.includes(Permissions.CREATE_PARTICIPANT)) {
    return;
  }
  const { participantDetails } = getState().participants;
  dispatch(slice.actions.setShowLoader(true));
  try {
    const response = await axios.get(`${BASE_URL}/events/api/v1/participants`);
    if (!response.data?.status) {
      throw new Error(response?.data?.message);
    }
    const filterParticipantData = (data: any) => {
      return data?.id !== participantDetails?.id;
    };
    if (participantDetails?.id) {
      const data = response?.data?.data.filter(filterParticipantData);
      dispatch(slice.actions.setParticipantsData(data));
    } else {
      dispatch(slice.actions.setParticipantsData(response?.data?.data));
    }
    dispatch(slice.actions.setShowLoader(false));
  } catch (error) {
    throw new Error(error);
  }
};

export const Roles = (Id: any) => async (dispatch) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/events/api/v1/events/roles`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          contextId: Id,
        },
      }
    );
    if (!response?.data?.status) {
      throw new Error(response?.data?.message);
    }
    const [defaultRole] = response.data.data.filter(
      (role) => role?.name === "Participant"
    );
    if (defaultRole) {
      dispatch(slice.actions.DefaultRole(defaultRole));
    }
    dispatch(slice.actions.RolesOptions(response?.data?.data));
  } catch (error) {
    throw new Error(error);
  }
};

export const addParticipant =
  (data, eventId, enqueueSnackbar) => async (dispatch) => {
    try {
      const participantsData = Array.isArray(data) ? data : [{ ...data }];
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/events/api/v1/participants?invite=true`,
        participantsData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            contextId: eventId,
          },
        }
      );
      if (!response?.data?.status) {
        enqueueSnackbar(response?.data?.message, {
        variant: "error",
        preventDuplicate: true,
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      }
     // removed here success snackbar
      dispatch(getParticipantsData());
    } catch (error) {
      enqueueSnackbar("Failed to add participant", {
        variant: "error",
        preventDuplicate: true,
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      dispatch(getParticipantsData());
      throw new Error(error);
    }
  };

export const EditParticipants =
  (participantId, eventId, participantData, participantDetail, enqueueSnackbar) => async (dispatch) => {
    try {
      const editParticipants = await axios.put(
        `${process.env.REACT_APP_API_URL}/events/api/v1/participants/${participantId}`,
        participantData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            contextId: eventId,
          },
        }
      );
      if (!editParticipants?.data?.status) {
        throw new Error(editParticipants?.data?.message);
      }
      // removed here success snackbar
      dispatch(resetParticipantDetails(participantDetail));
      // dispatch(getParticipantsData());
      dispatch(getParticipants());
      dispatch(getRolesAPI(eventId, participantData?.role?.name));
    } catch (error) {
        enqueueSnackbar("Failed to add participant", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 5000,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "right",
            },
          });
      throw new Error(error);
    }
  };

  export const DeleteParticipant =
  (participantId, eventId) => async (dispatch) => {
    try {
      const deleteParticipant = await axios.delete(
        `${process.env.REACT_APP_API_URL}/events/api/v1/participants?id=${participantId}&sendScheduleEmail=true`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            contextId: eventId,
          },
        }
      );
      if (!deleteParticipant?.data?.status) {
        throw new Error(deleteParticipant?.data?.message);
      }
      dispatch(getParticipantsData());
    } catch (error) {
     throw new Error(error);
    }
  };
  export const UnMuteAllParticipantMqtt =
  ( eventId,enqueueSnackbar) => async () => {
    try{
const response = await axios.post(
  `${process.env.REACT_APP_API_URL}/events/api/v1/events/ask-to-unmute`,
  {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            contextId: eventId,
          },
        }
);
if (!response?.data?.status) {
        throw new Error(response?.data?.message);
      }

    }catch(error){
       enqueueSnackbar("Failed to unmute participants", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 5000,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "right",
            },
          });
throw new Error(error);
    }
  };

export const reducer = slice.reducer;
